import { apiRequest } from './client.js';
import type { PlatformName } from './types.js';

/**
 * Connaro Widgets wire types (ARCHITECTURE.md §103).
 *
 * Hand-written, like every other file in this directory: the Console is a
 * client of a versioned HTTP contract, not a consumer of the server's internal
 * types (§46, §74). Importing `@connaro/widgets` here would let an internal
 * refactor silently change the browser's expectations.
 *
 * `config` is `unknown` on purpose. Its shape is the server's business — the
 * API validates on write and re-parses on read — and the editor below narrows
 * it per widget type rather than this file claiming to know all seven.
 */

export type WidgetCatalogEntry = {
  type: string;
  name: string;
  description: string;
  category: 'CHAT' | 'EVENTS' | 'GOALS' | 'INFORMATION';
  status: 'AVAILABLE' | 'RESTRICTED';
  /** Every message must name its platform (§23). Not operator-configurable. */
  requiresAttribution: boolean;
  /** Merchandising only: this type leads the gallery. Decided by the server. */
  featured: boolean;
  readsEvents: readonly string[];
  /** Every platform that could ever drive it. */
  supportedPlatforms: readonly PlatformName[];
  /** Those this workspace has connected. Empty means the card cannot be used. */
  availablePlatforms: readonly PlatformName[];
};

export type WidgetInstance = {
  id: string;
  widgetType: string;
  name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
};

export type WidgetGoalState = {
  startingValue: number;
  currentValue: number;
  goalStartedAt: string;
};

export async function fetchCatalog(): Promise<WidgetCatalogEntry[]> {
  const body = await apiRequest<{ widgets: WidgetCatalogEntry[] }>('/widgets/catalog');
  return body.widgets;
}

export async function fetchWidgets(): Promise<WidgetInstance[]> {
  const body = await apiRequest<{ widgets: WidgetInstance[] }>('/widgets');
  return body.widgets;
}

export async function fetchWidget(
  id: string,
): Promise<{ widget: WidgetInstance; goal: WidgetGoalState | null }> {
  return apiRequest<{ widget: WidgetInstance; goal: WidgetGoalState | null }>(`/widgets/${id}`);
}

export async function createWidget(input: {
  widgetType: string;
  name: string;
  config: Record<string, unknown>;
}): Promise<{ widget: WidgetInstance; token: string }> {
  return apiRequest<{ widget: WidgetInstance; token: string }>('/widgets', {
    method: 'POST',
    body: input,
  });
}

export async function updateWidget(
  id: string,
  patch: { name?: string; enabled?: boolean; config?: Record<string, unknown> },
): Promise<void> {
  await apiRequest(`/widgets/${id}`, { method: 'PATCH', body: patch });
}

export async function deleteWidget(id: string): Promise<void> {
  await apiRequest(`/widgets/${id}`, { method: 'DELETE' });
}

export async function rotateWidgetToken(id: string): Promise<string> {
  const body = await apiRequest<{ token: string }>(`/widgets/${id}/rotate-token`, {
    method: 'POST',
  });
  return body.token;
}

export async function resetWidgetGoal(id: string, startingValue?: number): Promise<void> {
  await apiRequest(`/widgets/${id}/reset-goal`, {
    method: 'POST',
    body: startingValue === undefined ? {} : { startingValue },
  });
}

/**
 * The browser-source URL for a freshly minted token.
 *
 * Built here rather than returned by the API because the API does not know
 * which origin the operator is looking at. The token is the only part that
 * matters and it came from the server.
 */
export function browserSourceUrl(token: string): string {
  return `${window.location.origin}/widget?k=${encodeURIComponent(token)}`;
}
